/**
 * Schritt „Regeln": welche Eigenschaften und Tags der Import schreibt (Phase 10).
 *
 * Drei Gestaltungsregeln aus §11 des Plans, hier konkret:
 *   1. Was schon richtig ist, bleibt unsichtbar — vorbelegt sind nur GPSR und Tag 203,
 *      der Rest des Katalogs liegt hinter „Eigenschaft hinzufügen".
 *   2. Pro Bildschirm eine Aufgabe.
 *   3. Keine nackten Plenty-Ids — jede Zeile trägt ihren Klartextnamen, die Id steht
 *      nur klein daneben.
 *
 * 🔴 Die Kategoriebedingung ist der Grund, warum es überhaupt mehr als eine flache
 * Liste braucht: die Druck-Marker „Vereinswappen", „Sponsor Brust" und „Sponsor
 * Rücken" gehören an Trikots, aber nicht an Torwarthandschuhe.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Save, Tag as TagIcon, Trash2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from './LaufButton';
import type {
  EigenschaftsRegel, KatalogAntwort, RegelMeldung, SchreibRegeln, TagRegel,
} from '../lib/typen';

export default function RegelnPanel({
  entwurfId, regeln, kategoriewerte, aufRegelnGeaendert,
}: {
  entwurfId: number;
  regeln: SchreibRegeln;
  kategoriewerte: { kategorie: string[]; filter: string[] };
  aufRegelnGeaendert: (regeln: SchreibRegeln) => void;
}) {
  const [katalog, setKatalog] = useState<KatalogAntwort | null>(null);
  const [entwurf, setEntwurf] = useState<SchreibRegeln>(regeln);
  const [meldungen, setMeldungen] = useState<RegelMeldung[]>([]);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => { setEntwurf(regeln); }, [regeln]);

  useEffect(() => {
    void api.get<{ data: KatalogAntwort }>('/programmes/artikelimport/katalog')
      .then(a => setKatalog(a.data.data))
      .catch(() => setFehler('Der Plenty-Katalog ließ sich nicht laden.'));
  }, []);

  // Alle Werte, auf die sich eine Bedingung beziehen kann. Beide Spalten in einer
  // Liste, weil der Nutzer nicht wissen muss, in welcher sein Wort steht.
  const bedingungsWerte = useMemo(() => {
    const alle = new Set([...kategoriewerte.filter, ...kategoriewerte.kategorie]);
    return Array.from(alle).sort((a, b) => a.localeCompare(b, 'de'));
  }, [kategoriewerte]);

  const schreibbare = useMemo(
    () => (katalog?.eigenschaften ?? []).filter(e => e.schreibbar),
    [katalog],
  );

  const speichern = useCallback(async () => {
    setSpeichert(true);
    setFehler(null);
    try {
      const antwort = await api.patch<{ data: { regeln: SchreibRegeln; meldungen: RegelMeldung[] } }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/regeln`,
        { regeln: entwurf },
      );
      setMeldungen(antwort.data.data.meldungen);
      aufRegelnGeaendert(antwort.data.data.regeln);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  }, [entwurfId, entwurf, aufRegelnGeaendert]);

  const setzeEigenschaft = (index: number, teil: Partial<EigenschaftsRegel>) => {
    setEntwurf(v => ({
      ...v,
      eigenschaften: v.eigenschaften.map((e, i) => (i === index ? { ...e, ...teil } : e)),
    }));
  };

  const fehlerZu = (regel: EigenschaftsRegel) =>
    meldungen.find(m => m.art === 'fehler' && m.propertyId === regel.propertyId);

  return (
    <section className="space-y-4 rounded border border-[#DAD9D9] p-4">
      <div>
        <h2 className="font-semibold text-[#1A1A1A]">Eigenschaften und Tags</h2>
        <p className="mt-1 text-sm text-[#1A1A1A]/70">
          Was jeder angelegten Variante mitgegeben wird. Vorbelegt sind die
          GPSR-Bescheinigung und Tag 203 „aktiv in Webshopsuche" — beides war bisher
          Handarbeit und wurde im Bestand erst Wochen nach der Anlage gesetzt. Alles
          Weitere ist freiwillig.
        </p>
      </div>

      {fehler && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {fehler}
        </div>
      )}

      {/* ── Eigenschaften ──────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-[#1A1A1A]">
          Eigenschaften ({entwurf.eigenschaften.length})
        </h3>

        {entwurf.eigenschaften.length === 0 && (
          <p className="text-sm text-[#1A1A1A]/60">
            Keine Eigenschaft gewählt — die Varianten entstehen ohne.
          </p>
        )}

        {entwurf.eigenschaften.map((regel, i) => {
          const eigenschaft = schreibbare.find(e => e.id === regel.propertyId);
          const problem = fehlerZu(regel);
          return (
            <div
              key={`${regel.propertyId}-${i}`}
              className={`flex flex-wrap items-center gap-2 rounded border p-2 ${
                problem ? 'border-red-300 bg-red-50/40' : 'border-[#DAD9D9]'
              }`}
            >
              <span className="min-w-[14rem] flex-1 text-sm">
                <span className="font-medium text-[#1A1A1A]">
                  {eigenschaft?.name ?? `Eigenschaft ${regel.propertyId}`}
                </span>
                <span className="ml-2 text-xs text-[#1A1A1A]/40">
                  {regel.propertyId}
                  {eigenschaft && ` · ${eigenschaft.cast}`}
                  {eigenschaft?.gruppeName && ` · ${eigenschaft.gruppeName}`}
                </span>
              </span>

              <Wertfeld
                cast={eigenschaft?.cast}
                auswahl={eigenschaft?.auswahl ?? []}
                regel={regel}
                aufAenderung={teil => setzeEigenschaft(i, teil)}
              />

              <Bedingung
                werte={bedingungsWerte}
                gewaehlt={regel.nurBeiKategorie ?? []}
                aufAenderung={nurBeiKategorie => setzeEigenschaft(i, { nurBeiKategorie })}
              />

              <button
                type="button"
                title="Entfernen"
                onClick={() => setEntwurf(v => ({
                  ...v, eigenschaften: v.eigenschaften.filter((_, j) => j !== i),
                }))}
                className="rounded p-1 text-[#1A1A1A]/50 hover:bg-[#F7F7F5] hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>

              {problem && (
                <p className="w-full text-xs text-red-800">{problem.text}</p>
              )}
            </div>
          );
        })}

        <Hinzufuegen
          platzhalter="Eigenschaft hinzufügen …"
          eintraege={schreibbare
            .filter(e => !entwurf.eigenschaften.some(r => r.propertyId === e.id))
            .map(e => ({ id: e.id, name: e.name, zusatz: e.cast }))}
          aufWahl={id => setEntwurf(v => ({
            ...v, eigenschaften: [...v.eigenschaften, { propertyId: id }],
          }))}
        />
      </div>

      {/* ── Tags ───────────────────────────────────────────────────────────── */}
      <div className="space-y-2 border-t border-[#DAD9D9] pt-4">
        <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
          <TagIcon className="h-4 w-4" /> Tags ({entwurf.tags.length})
        </h3>

        {entwurf.tags.map((regel, i) => (
          <div
            key={`${regel.tagId}-${i}`}
            className="flex flex-wrap items-center gap-2 rounded border border-[#DAD9D9] p-2"
          >
            <span className="min-w-[14rem] flex-1 text-sm">
              <span className="font-medium text-[#1A1A1A]">
                {katalog?.tags.find(t => t.id === regel.tagId)?.name ?? `Tag ${regel.tagId}`}
              </span>
              <span className="ml-2 text-xs text-[#1A1A1A]/40">{regel.tagId}</span>
            </span>

            <Bedingung
              werte={bedingungsWerte}
              gewaehlt={regel.nurBeiKategorie ?? []}
              aufAenderung={nurBeiKategorie => setEntwurf(v => ({
                ...v,
                tags: v.tags.map((t, j) => (j === i ? { ...t, nurBeiKategorie } : t)),
              }))}
            />

            <button
              type="button"
              title="Entfernen"
              onClick={() => setEntwurf(v => ({ ...v, tags: v.tags.filter((_, j) => j !== i) }))}
              className="rounded p-1 text-[#1A1A1A]/50 hover:bg-[#F7F7F5] hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}

        <Hinzufuegen
          platzhalter="Tag hinzufügen …"
          eintraege={(katalog?.tags ?? [])
            .filter(t => !entwurf.tags.some(r => r.tagId === t.id))
            .map(t => ({ id: t.id, name: t.name }))}
          aufWahl={(id: number) => setEntwurf(v => ({ ...v, tags: [...v.tags, { tagId: id }] }))}
        />
      </div>

      {/* ── Speichern ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 border-t border-[#DAD9D9] pt-4">
        <LaufButton
          laeuft={speichert}
          laufText="Regeln werden geprüft und gespeichert"
          dauerHinweis="jede Regel wird gegen den Plenty-Katalog geprüft"
          icon={<Save className="mr-2 h-4 w-4" />}
          onClick={() => void speichern()}
          disabled={!katalog}
          className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
        >
          Regeln speichern
        </LaufButton>
        {katalog && (
          <span className="text-xs text-[#1A1A1A]/50">
            Katalog: {katalog.eigenschaften.filter(e => e.schreibbar).length} Eigenschaften,{' '}
            {katalog.tags.length} Tags
          </span>
        )}
      </div>

      {meldungen.filter(m => m.art !== 'fehler').map((m, i) => (
        <p key={i} className="text-sm text-amber-800">{m.text}</p>
      ))}
      {meldungen.some(m => m.art === 'fehler') && (
        <p className="text-sm text-red-800">
          Solange ein Fehler drinsteht, verweigert der Schreiblauf den Start — die
          Regeln würden sonst an jeder Variante scheitern.
        </p>
      )}
    </section>
  );
}

/** Das Eingabefeld, das zum Cast passt. Ein Marker bekommt gar keines. */
function Wertfeld({
  cast, auswahl, regel, aufAenderung,
}: {
  cast?: string;
  auswahl: Array<{ id: number; wert: string }>;
  regel: EigenschaftsRegel;
  aufAenderung: (teil: Partial<EigenschaftsRegel>) => void;
}) {
  if (cast === 'empty') {
    return (
      <span className="text-xs text-[#1A1A1A]/50" title="Die bloße Zuordnung ist die Aussage.">
        Marker, kein Wert
      </span>
    );
  }

  if (cast === 'selection' || cast === 'multiSelection') {
    return (
      <select
        value={regel.auswahlId ?? ''}
        onChange={e => aufAenderung({ auswahlId: e.target.value ? Number(e.target.value) : null })}
        className="rounded border border-[#DAD9D9] px-2 py-1 text-sm"
      >
        <option value="">Wert wählen …</option>
        {auswahl.map(a => (
          <option key={a.id} value={a.id}>{a.wert}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={regel.wert ?? ''}
      onChange={e => aufAenderung({ wert: e.target.value })}
      placeholder={cast === 'int' || cast === 'float' ? 'Zahl' : 'Wert'}
      className="w-48 rounded border border-[#DAD9D9] px-2 py-1 text-sm"
    />
  );
}

/**
 * Die Kategoriebedingung.
 *
 * Mehrfachauswahl über die Werte, die in DIESEM Entwurf wirklich vorkommen — nicht
 * über eine gepflegte Liste, die veralten kann. Nichts gewählt heißt „für alle".
 */
function Bedingung({
  werte, gewaehlt, aufAenderung,
}: {
  werte: string[];
  gewaehlt: string[];
  aufAenderung: (werte: string[] | undefined) => void;
}) {
  const [offen, setOffen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOffen(o => !o)}
        className={`rounded border px-2 py-1 text-xs ${
          gewaehlt.length > 0
            ? 'border-[#1A1A1A] bg-[#E9FF57]/40 text-[#1A1A1A]'
            : 'border-[#DAD9D9] text-[#1A1A1A]/60'
        }`}
        title={gewaehlt.length > 0 ? gewaehlt.join('\n') : 'Gilt für alle Artikel'}
      >
        {gewaehlt.length === 0 ? 'für alle' : `nur ${gewaehlt.length} Kategorien`}
      </button>

      {offen && (
        <div className="absolute right-0 z-10 mt-1 max-h-64 w-80 overflow-y-auto rounded border border-[#DAD9D9] bg-white p-2 shadow-lg">
          <p className="mb-2 text-xs text-[#1A1A1A]/60">
            Nichts angehakt = gilt für alle. Geprüft werden Produktkategorie und
            Filterspalte.
          </p>
          {werte.length === 0 && (
            <p className="text-xs text-[#1A1A1A]/60">
              Dieser Entwurf führt keine Kategoriewerte.
            </p>
          )}
          {werte.map(wert => (
            <label key={wert} className="flex cursor-pointer items-center gap-2 py-0.5 text-xs">
              <input
                type="checkbox"
                checked={gewaehlt.includes(wert)}
                onChange={e => {
                  const neu = e.target.checked
                    ? [...gewaehlt, wert]
                    : gewaehlt.filter(w => w !== wert);
                  aufAenderung(neu.length > 0 ? neu : undefined);
                }}
              />
              <span>{wert}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Suchbare Auswahl über den Katalog — 95 Eigenschaften und 428 Tags. */
function Hinzufuegen({
  platzhalter, eintraege, aufWahl,
}: {
  platzhalter: string;
  eintraege: Array<{ id: number; name: string; zusatz?: string }>;
  aufWahl: (id: number) => void;
}) {
  const [suche, setSuche] = useState('');
  const treffer = useMemo(() => {
    const s = suche.trim().toLowerCase();
    if (s.length === 0) return [];
    return eintraege
      .filter(e => e.name.toLowerCase().includes(s) || String(e.id) === s)
      .slice(0, 12);
  }, [suche, eintraege]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Plus className="h-4 w-4 text-[#1A1A1A]/40" />
        <input
          type="text"
          value={suche}
          onChange={e => setSuche(e.target.value)}
          placeholder={platzhalter}
          className="w-72 rounded border border-[#DAD9D9] px-2 py-1 text-sm"
        />
      </div>
      {treffer.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-64 w-96 overflow-y-auto rounded border border-[#DAD9D9] bg-white shadow-lg">
          {treffer.map(e => (
            <button
              key={e.id}
              type="button"
              onClick={() => { aufWahl(e.id); setSuche(''); }}
              className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-[#F7F7F5]"
            >
              <span className="flex-1">{e.name}</span>
              <span className="text-xs text-[#1A1A1A]/40">
                {e.id}{e.zusatz && ` · ${e.zusatz}`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
